<template>
	<!-- Noeud avec ses enfants
	generateVariantStyle() permet de générer les variables Css dynamique utilisable dans la section <style></style> -->
	<li class="Node" :style="generateVariantStyle(Variant)">
		<!-- Seulement la ligne de noeud -->
		<b-row class="p-0 m-0 NodeInfoLine" v-bind:class="{ DashNode: (Node.Id === dashNode.Id) }" @click="setDashNode()">
			<!-- Flèche, icone et libellé du noeud -->
			<b-col class="p-0 m-0" cols="10">
				<span class="mr-2"><!-- Réserve l'espace du caret même si le noeud n'a pas d'enfant -->
					<!-- Icone sur la gauche du noeud pour déployer ou replier les noeuds enfants -->
					<span @click.stop.prevent="toggleExpand()">
						<b-icon icon="caret-down-fill" v-if="Node.Expanded" :style="{ visibility: Node.Children.length > 0 ? 'visible' : 'hidden' }" />
						<b-icon icon="caret-right" v-else :style="{ visibility: Node.Children.length > 0 ? 'visible' : 'hidden' }"/>
					</span>
				</span>
				<span>
					<b-img :src="AbsoluteLink('Icons/' + Node.Icon)" v-if="Node.Icon" class="m-0" />
					<!-- Ajoute une coche en haut à droite de l'icone si on tableau de bord par défaut existe pour le type objet -->
					<!-- <b-icon v-if="node.Nodetype == nodeTypeEnum.ObjectType && (Number(node.Id) > 0)" icon="check-circle-fill" scale="0.75" shift-h="2" shift-v="6" :class="'p-0 m-0 text-' + Variant"></b-icon> -->
					<span v-bind:class="{ 'font-weight-bold': (Number(Node.Id) > 0) }">
						{{ Node.Name }}
					</span>
				</span>
			</b-col>
			<!-- Boutons en bout de ligne -->
			<b-col class="p-0 m-0" cols="2">
				<!-- Bouton d'ajout d'un dashboard -- Inutile de placer l'ajout sur les noeuds ObjectType les plus haut (OL, OML, equipement) -->
				 <b-button v-if="$store.getters['CheckAction']('Add', undefined, Action)
								&& Node.Nodetype == nodeTypeEnum.ObjectType 
								&& (Node.Children.length === 0 || (Node.Children.length > 0 
									&& Node.Children[0].Nodetype !== nodeTypeEnum.ObjectType))" 
					size="sm" :variant="'outline-' + Variant" 
					@click.stop.prevent="add()" centered 
					class="border-0 bg-white p-0 m-0 mr-2 float-right NodeAddDashboard" style="height: 18px;">
					<b-icon icon="plus" size="sm" class="m-0" />
				</b-button>
				<!-- Bouton de suppression du dashboard -->
				<b-button v-if="$store.getters['CheckAction']('Delete', undefined, Action)
								&& Node.Nodetype == nodeTypeEnum.Dashboard"
					size="sm" :variant="'outline-' + Variant" 
					@click.stop.prevent="remove()" centered 
					class="border-0 bg-white p-0 m-0 mr-2 float-right NodeRemoveDashboard" style="height: 18px;">
					<b-icon icon="trash" size="sm" class="m-0" align="center" />
				</b-button>
			</b-col>
		</b-row>
		<!-- Liste des noeuds enfant du noeud courant -->
		<transition name="node-opening">
			<ul v-if="Node.Expanded || Node.Forced">
				<dash-node v-for="nodeChild in Node.Children" v-bind:node="nodeChild" v-bind:key="nodeChild.Id" :ParentNode="Node" :Action="Action" />
			</ul>
		</transition>
	</li>
</template>

<script lang="ts">
import Component, { mixins } from 'vue-class-component';
import { InjectReactive, Prop } from 'vue-property-decorator';
import { Getter } from 'vuex-class';
import BaseTreeNode from '@/types/BaseTreeNode';
import NodeTypeEnum from '@/enums/NodeTypeEnum';
import { LinkUtils, ThemeUtils } from "@/components/commons/Utils";
import locales from './locales/DashNode.json';
import { BvMsgBoxData } from 'bootstrap-vue';
import SaveModeEnum from '@/enums/SaveModeEnum';

/** Noeud de l'arborescence DashTree qui peut être soit un type objet soit un dashboard objet existant */
@Component({
	name: 'dash-node',
	components: {
		DashNode
	},
	i18n:{messages:locales}
})
export default class DashNode extends mixins( LinkUtils, ThemeUtils ) {
	/** Variant de couleur à utiliser sur le tableau de bord */
	@InjectReactive()
	private Variant!: string;

	/** Droit disponible pour l'utilisateur en cours */
	@Prop()
	public Action!: string;

	/** Nécessaire pour pouvoir utiliser cet enum dans la section template */
	private nodeTypeEnum = NodeTypeEnum;

	/** Noeud parent */
	@Prop({ default: undefined })
	public ParentNode!: BaseTreeNode;

	/** Noeud associé au composant en cours */
	@Prop()
	public Node!: BaseTreeNode;

	/** Noeud sélectionné dans l'arborescence */
	@Getter('ObjectDashboardStore/GetDashNode')
	private dashNode!: BaseTreeNode;

	/** Indique si le tableau de bord a été modifié */
	@Getter('ObjectDashboardStore/GetIsModified')
	private dashboardIsModified!: boolean;

	/** Ouvre ou ferme la liste des enfants associé au noeud courant (node) */
    private toggleExpand() {
		this.Node.Expanded = !this.Node.Expanded;
	}

	/** Applique le noeud associé au composant (node) comme le noeud courant (dashNode) */
	private async setDashNode() {
		try {

			let dstDashNode : BaseTreeNode = this.Node;
			if(this.Node && !this.Node.DashboardRef && this.Node.Children.length > 0 && this.Node.Children[0].DashboardRef)
				dstDashNode = dstDashNode.Children[0];
			
			if(!await this.warningBeforeChangeNode(dstDashNode))
				return;
			await this.$store.dispatch("ShowLoadingMask", this.$t('ObjectDashboardLoading'));    // "Chargement du tableau de bord objet..."

			// Selectionne un dashboard dans l'arborescence des types de noeud
			await this.$store.dispatch("ObjectDashboardStore/SelectDashNode", dstDashNode)

		} catch(err) {
			console.error(err);
			this.$bvToast.toast(this.$t('AnErrorOccured').toString(), { // $t('AnErrorOccured') / "Une erreur s'est produite."
				title: this.$t('Error').toString(), // $t('Error') / "Erreur"
				autoHideDelay: 10000,
				variant: "danger",
				solid: true,
				appendToast: true,
				toaster: "b-toaster-top-center"
			});
		}
		finally{
            await this.$store.dispatch("HideLoadingMask", {});
		}
	}

	/** Ajoute un dashboard subalterne de ce noeud */
	private async add() {
		if(!await this.warningBeforeChangeNode())
				return;
		await this.$store.dispatch("ShowLoadingMask", this.$t('ObjectDashboardAdd'));    // "Ajout d'un tableau de bord objet..."
		try{
			await this.$store.dispatch("ObjectDashboardStore/CreateDashboard", { dashNode: this.Node, profileIds: [], nodeIds: [], widgets: [] });
		}finally{
			await this.$store.dispatch("HideLoadingMask", {});
		}
	}

	/** Suppression d'un objectDashboard */
	private async remove() {
		const confirm: BvMsgBoxData = await this.$bvModal.msgBoxConfirm(this.$t('ConfirmDeleteDashboard').toString(), { 
			title: this.$t('ObjectDashboard').toString(), okVariant: 'danger', okTitle: this.$t('Yes').toString(), cancelTitle: this.$t('No').toString(), hideHeaderClose: false, centered: true
		});

		if (confirm) {
        	await this.$store.dispatch("ShowLoadingMask", this.$t('ObjectDashboardDelete'));    // "Suppression du tableau de bord objet..."

			try{
				await this.$store.dispatch("ObjectDashboardStore/Delete", this.Node)
				this.$bvToast.toast(this.$t('SuccessDeleteDashboard').toString(), { // $t('SuccessDeleteDashboard') / "Le tableau de bord a été supprimé avec succès"
					title: this.$t('DeleteDashboard').toString(), // $t('DeleteDashboard') / "Suppression tableau de bord",
					autoHideDelay: 5000,
					variant: "success",
					solid: true,
					appendToast: true,
					toaster: "b-toaster-top-center"
				});

				if(this.Node.Id === this.dashNode.Id && this.ParentNode !== undefined)
					// Selectionne un dashboard dans l'arborescence des types de noeud
					await this.$store.dispatch("ObjectDashboardStore/SelectDashNode", this.ParentNode)
			}catch(err){
				console.error(err);
				this.$bvToast.toast(this.$t('AnErrorOccured').toString(), { // $t('AnErrorOccured') / "Une erreur s'est produite."
					title: this.$t('Error').toString(), // $t('Error') / "Erreur"
					autoHideDelay: 10000,
					variant: "danger",
					solid: true,
					appendToast: true,
					toaster: "b-toaster-top-center"
				});
			}
			finally{
            	await this.$store.dispatch("HideLoadingMask", {});
			}
		}
	}

	/**
	 * Demande à l'utilisateur si il est d'accord pour perdre ses données non sauvegardé sur son tableau de bord en cours.
	 * @param dstDashNode Noeud qui sera selectionné si l'utilisateur confirme
	 * @returns True l'utilisateur confirme | False si il souhaite annuler l'opération
	 */
	private async warningBeforeChangeNode(dstDashNode : BaseTreeNode = undefined) : Promise<boolean>{
		if(this.dashboardIsModified ){
			if(dstDashNode && dstDashNode.DashboardRef < 0) // Ne pas recharger un noeud en cours de creation
				return;
			const confirm: BvMsgBoxData = await this.$bvModal.msgBoxConfirm(this.$t('ConfirmSkipChangeDashboard').toString(), { 
				title: this.$t('ObjectDashboard').toString(), okVariant: 'danger', okTitle: this.$t('Yes').toString(), cancelTitle: this.$t('No').toString(), hideHeaderClose: false, centered: true
			});
			/** Si l'utilisateur ne souhaite pas perdre ses modifications, alors nous devons stopper la selection. */
			if(!confirm) return false;

			/** Détecte un nouveau tableau non sauvegardé et le supprime si nécessaire. */
			const saveMode: SaveModeEnum = this.$store.getters['ObjectDashboardStore/GetSaveMode'];
			if(saveMode == SaveModeEnum.NewObject) {
				await this.$store.dispatch("ShowLoadingMask", this.$t('ObjectDashboardDelete'));    // "Suppression du tableau de bord objet..."
				try{
					let oldNode = this.$store.getters['ObjectDashboardStore/GetDashNode'];
					// On purge l'arbre en forçant le rechargement (permet aussi de rafraichir l'affichage si un tableau est de créé par un autre login)
					await this.$store.dispatch("ObjectDashboardStore/Delete", oldNode)
				}finally{
					await this.$store.dispatch("HideLoadingMask", {});
				}
			}
		}
		return true;
	}
}
</script>

<style scoped>
.Node {
	position: relative;
	display: block;
	box-sizing: border-box;
	padding-left: 10px;
	right: 0px;
	transition-duration: 0.15s;
	/* vertical-align: text-top; */
	border-right: 0px;
	border-radius: 3px;
}

.Node img {
	display: inline-block;
	width: 16px;
	height: 16px;
	margin: 0px 10px 0px 0px;
}

.Node:hover {
	cursor: pointer;
}

.Node.Selected {
	border: 3px solid rgb(18, 177, 186);
}

ul {
	box-sizing: border-box;
	/* line-snap: none; */
	padding-inline-start: 10px !important;
	writing-mode: horizontal-tb;
	padding-left: 10px !important;
	width: 100%;
	height: 100%;
	margin-top: 0;
	margin-bottom: 0;
}

li {
	list-style: none;
	list-style-position: inside;
	border-top: 0px;
	border-bottom: 0px;
}

.DashNode {
	background-color: var(--VariantLight);
	color: var(--Variant);
}

.NodeInfoLine:hover {
	background-color: lightgrey;
	/*filter: grayscale(100%) saturate(140%);*/ /* brightness(60%) saturate(140%); */
}

.NodeInfoLine:hover div .NodeAddDashboard {
	opacity: 1;
	visibility: visible;
}

.NodeAddDashboard {
	opacity: 0;
	visibility: hidden;
}

.NodeInfoLine:hover div .NodeRemoveDashboard {
	opacity: 1;
	visibility: visible;
}

.NodeRemoveDashboard {
	opacity: 0;
	visibility: hidden;
}
</style>